import React from "react";
import { Link } from "react-router-dom";
import { useContent } from "@/lib/content";
import MiraGate from "@/components/MiraGate";
import PilotForm from "@/components/PilotForm";

const HERO_IMAGE = "https://res.cloudinary.com/datmqdk2c/image/upload/f_auto,q_auto/lc/hero/hero-butler.jpg";

const SCROLL = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const services = [
    {
        slug: "everyday-life",
        title: "Everyday Life Concierge®",
        body: "For the personal tasks that quietly eat into an employee's day. Restaurants, errands, gifting, courier, travel, home repairs, bills, appointments, last-minute needs.",
    },
    {
        slug: "family-care",
        title: "Family & Care Support",
        body: "Because employees are also parents, children, partners and caregivers. Senior parent support, children, doctor appointments, family travel, household responsibilities.",
    },
    {
        slug: "travel-mobility",
        title: "Travel, Mobility & Relocation Concierge®",
        body: "For employees who are constantly moving. Flights, hotels, visa and passport coordination, airport support, relocation, city orientation, travel disruption assistance.",
    },
    {
        slug: "health-wellness",
        title: "Health, Wellness & Emergency Assistance",
        body: "Practical help when employees or their families need support most. Doctor appointments, diagnostics, pharmacy, hospital information, urgent household and emergency response.",
    },
    {
        slug: "documentation-government",
        title: "Documentation & Government Assistance",
        body: "For the work that is often stressful and confusing. Passport, visa, PAN, Aadhaar, driving licence, certificates, municipal work, registrations, document pickup.",
    },
    {
        slug: "moments-celebrations",
        title: "Moments, Celebrations & Experiences",
        body: "Because wellbeing is also about helping people enjoy life. Birthdays, anniversaries, festivals, getaways, gifting, premium dining, tickets and special occasion support.",
    },
];

const personas = [
    {
        eyebrow: "For Everyday Employees",
        lede: "We help employees manage the personal tasks that quietly steal time, focus and energy from the workday.",
        body: "From errands, couriers, bookings, repairs, gifting and appointments to travel planning, purchases and local assistance, Les Concierges® becomes the trusted hand employees can reach for when life gets busy. So they do not have to spend office hours chasing vendors, waiting on calls, searching for contacts or figuring things out alone.",
        close: "Less stress. More focus. A workday that feels lighter.",
    },
    {
        eyebrow: "For Employees With Families",
        lede: "We support the responsibilities employees carry beyond themselves.",
        body: "Parents. Children. Spouses. Ageing family members. Homes. Health needs. School requirements. Celebrations. Urgent errands. Household coordination. Whether it is arranging a doctor's appointment for a parent, planning a child's birthday, helping with school-related needs, coordinating medicines, managing a home repair or assisting during an emergency, Les Concierges® helps employees stay present at work without feeling absent from home.",
        close: "Because work-life balance is not a slogan. It is support when life actually happens.",
    },
    {
        eyebrow: "For Employees on the Move",
        lede: "We help employees travel, relocate and navigate life across cities and countries with confidence.",
        body: "Flights, hotels, visa and passport support, airport assistance, cab coordination, relocation help, city orientation, local services, emergency travel changes and family travel planning. Whether an employee is shifting to a new city, travelling for work, planning a family holiday or handling a last-minute disruption, Les Concierges® brings calm, clarity and human follow-through.",
        close: "Across India. Across 150+ countries. Same care. Same standard.",
    },
];

const lifeMoments = [
    "The errands.",
    "The documents.",
    "The parent who needs help.",
    "The child's school requirement.",
    "The passport renewal.",
    "The broken appliance.",
    "The last-minute gift.",
    "The appointment that cannot wait.",
    "The family emergency that lands in the middle of a workday.",
];

const promiseDenials = [
    "We are not another app.",
    "We are not a helpline.",
    "We are not a vendor list.",
    "We are not a perk marketplace.",
];

export default function Home() {
    const { content } = useContent();
    const v = (k, fallback = "") => content[k] || fallback;

    return (
        <>
            {/* 01 — HERO */}
            <section
                id="hero"
                className="relative min-h-[100svh] flex items-center overflow-hidden"
                data-testid="section-hero"
                style={{ background: "hsl(var(--navy))" }}
            >
                <div className="absolute inset-0">
                    <img
                        src={HERO_IMAGE}
                        alt="A concierge in formal attire holding a gilded service tray"
                        className="w-full h-full object-cover reveal-slow"
                        style={{ objectPosition: "left center", filter: "brightness(0.95) contrast(1.02)" }}
                    />
                    {/* Right-side gradient so headline reads cleanly over the lighter top-right of the image */}
                    <div
                        className="absolute inset-0"
                        style={{
                            background:
                                "linear-gradient(90deg, hsl(222 47% 8% / 0.0) 0%, hsl(222 47% 8% / 0.45) 35%, hsl(222 47% 9% / 0.85) 70%, hsl(222 47% 9% / 0.94) 100%)",
                        }}
                    />
                    <div
                        className="absolute inset-x-0 bottom-0 h-40"
                        style={{
                            background:
                                "linear-gradient(to bottom, hsl(var(--ivory) / 0) 0%, hsl(var(--ivory)) 100%)",
                        }}
                    />
                </div>

                <div className="relative container-prose w-full pt-32 md:pt-24 pb-24">
                    <div className="md:ml-[42%] md:max-w-2xl">
                        <p
                            className="eyebrow reveal"
                            style={{ color: "hsl(var(--brass-soft))" }}
                            data-testid="hero-eyebrow"
                        >
                            Les Concierges · Established 1998 · The registered concierge® of India
                        </p>
                        <h1
                            className="font-serif mt-7 text-[40px] md:text-[60px] lg:text-[72px] leading-[1.04] tracking-tight reveal"
                            style={{ color: "hsl(var(--ivory))", animationDelay: "0.25s" }}
                            data-testid="hero-headline"
                        >
                            {v("hero_headline")}
                        </h1>
                        <p
                            className="mt-7 text-[16px] md:text-[18px] leading-relaxed max-w-xl reveal"
                            style={{ color: "hsl(var(--ivory) / 0.78)", animationDelay: "0.45s" }}
                            data-testid="hero-subheadline"
                        >
                            {v("hero_subheadline")}
                        </p>
                        <div
                            className="mt-10 flex flex-col sm:flex-row gap-4 reveal"
                            style={{ animationDelay: "0.65s" }}
                        >
                            <a
                                href="#mira"
                                onClick={SCROLL("mira")}
                                className="btn-primary btn-primary-on-dark"
                                data-testid="hero-cta-talk-to-mira"
                            >
                                Talk to Mira
                                <span aria-hidden>→</span>
                            </a>
                            <a
                                href="#pilot"
                                onClick={SCROLL("pilot")}
                                className="btn-ghost btn-ghost-on-dark"
                                data-testid="hero-cta-enterprise"
                            >
                                Speak to our Enterprise Team
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* 02 — MIRA, LIVE AND WAITING */}
            <section id="mira" className="section" data-testid="section-mira">
                <div className="container-prose">
                    <div className="text-center max-w-2xl mx-auto">
                        <p className="eyebrow">Section 02 · Live</p>
                        <h2 className="font-serif text-4xl md:text-5xl mt-4">{v("mira_intro_above")}</h2>
                        <p className="mt-6 text-[16px] md:text-[18px] leading-relaxed text-[hsl(var(--ink))]">
                            {v("mira_intro_below")}
                        </p>
                    </div>

                    <div className="mt-14 max-w-5xl mx-auto">
                        <MiraGate />
                    </div>

                    <p
                        className="mt-10 max-w-3xl mx-auto text-center text-[14px] md:text-[15px] italic leading-relaxed"
                        style={{ color: "hsl(var(--muted))" }}
                        data-testid="mira-handoff-note"
                    >
                        {v("mira_handoff_note")}
                    </p>
                </div>
            </section>

            {/* 02b — THE HUMAN OFFICE BEHIND MIRA */}
            <section
                className="section"
                style={{ background: "hsl(var(--paper))" }}
                data-testid="section-human-office"
            >
                <div className="container-prose max-w-3xl">
                    <p className="eyebrow">Section 02b · Reassurance</p>
                    <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight" data-testid="human-office-heading">
                        The human office <span className="italic text-[hsl(var(--brass))]">behind Mira.</span>
                    </h2>

                    <div className="mt-12 space-y-7 text-[16px] md:text-[17px] leading-[1.75] text-[hsl(var(--ink))]" data-testid="human-office-body">
                        <p className="font-serif text-[20px] md:text-[24px] leading-snug text-[hsl(var(--navy))]">
                            Behind Mira is not a call centre.<br />
                            It is a trained concierge® team built over twenty-eight years of real service.
                        </p>

                        <p>
                            Our people understand that every request has a person behind it: an employee trying to manage home and work, a family member handling something urgent, a resident needing quiet support, a relationship manager protecting a client promise.
                        </p>

                        <div className="grid md:grid-cols-2 gap-x-12 gap-y-3 py-4">
                            <p className="font-serif italic text-[18px] md:text-[20px] text-[hsl(var(--navy))]">
                                Mira gathers the request.
                            </p>
                            <p className="text-[15px] md:text-[16px] leading-[1.7] text-[hsl(var(--ink))]">
                                Our team reads the context, confirms what is needed, coordinates with the right people, follows up, escalates when required, and closes the loop.
                            </p>
                        </div>

                        <p className="font-serif italic text-[18px] text-[hsl(var(--brass))]" data-testid="human-office-difference">
                            That is the Les Concierges® difference.
                        </p>

                        <p>
                            Technology may make the first response faster. But judgement, discretion, warmth, and accountability still come from people.
                        </p>
                    </div>

                    <div className="mt-14 pt-8 border-t border-[hsl(var(--brass)/0.4)]">
                        <p className="font-serif italic text-[22px] md:text-[26px] text-[hsl(var(--navy))]" data-testid="human-office-couplet">
                            The AI listens. <span className="text-[hsl(var(--brass))]">The humans deliver.</span>
                        </p>
                    </div>
                </div>
            </section>

            {/* 03 — THE HYBRID */}
            <section
                className="section-tight"
                style={{ background: "hsl(var(--cream))" }}
                data-testid="section-hybrid"
            >
                <div className="container-prose">
                    <p className="eyebrow text-center">Section 03 · The hybrid that nobody else has</p>
                    <div className="mt-14 grid md:grid-cols-3 gap-12 md:gap-16">
                        {[
                            {
                                num: "01",
                                title: "AI listens, twenty-four hours.",
                                body: "Mira gathers context, anticipates need, and structures every request — at three in the morning, in any language your employees speak.",
                            },
                            {
                                num: "02",
                                title: "Concierges® deliver.",
                                body: "Trained concierges® in our offices across India confirm availability, negotiate access, and close every loop — anywhere your people are. Real humans, in 150+ countries.",
                            },
                            {
                                num: "03",
                                title: "You see everything.",
                                body: "Live dashboards show what your people asked, what got delivered, and what they are loving most. Productivity up. Stress down. Loyalty, measured.",
                            },
                        ].map((c) => (
                            <div key={c.num} data-testid={`hybrid-col-${c.num}`}>
                                <p
                                    className="font-serif text-[hsl(var(--brass))] text-3xl"
                                    style={{ fontStyle: "italic" }}
                                >
                                    {c.num}
                                </p>
                                <h3 className="font-serif text-2xl md:text-[28px] leading-snug mt-3">
                                    {c.title}
                                </h3>
                                <p className="mt-4 text-[15px] leading-relaxed text-[hsl(var(--ink))]">
                                    {c.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 04 — CREDENTIALS */}
            <section className="section-tight" data-testid="section-credentials">
                <div className="container-prose">
                    <div className="gold-rule" />
                    <p
                        className="font-serif italic text-[22px] md:text-[28px] leading-relaxed max-w-5xl mx-auto text-center py-14 md:py-20"
                        data-testid="credentials-line"
                    >
                        {v("credentials_line")}
                    </p>
                    <div className="gold-rule" />
                </div>
            </section>

            {/* 04.25 — TRUSTED BY (MARQUEE CLIENTS) */}
            <section
                className="section-tight"
                data-testid="section-marquee-clients"
                style={{ background: "hsl(var(--cream))", paddingTop: "5rem", paddingBottom: "5rem" }}
            >
                <div className="container-prose">
                    <div className="md:flex md:items-end md:justify-between gap-12 mb-14">
                        <div>
                            <p className="eyebrow">Trusted by</p>
                            <h2 className="font-serif text-3xl md:text-[42px] leading-tight mt-4 max-w-2xl">
                                Some of our <span className="italic text-[hsl(var(--brass))]">marquee clients.</span>
                            </h2>
                        </div>
                        <p className="mt-6 md:mt-0 max-w-md text-[14px] md:text-[15px] leading-relaxed text-[hsl(var(--ink))]">
                            Three hundred-plus corporate clients have come to us, quietly, since 1998. A few you may recognise.
                        </p>
                    </div>

                    <div className="gold-rule" style={{ background: "hsl(var(--brass) / 0.35)" }} />

                    <ul
                        className="grid grid-cols-2 md:grid-cols-4 gap-x-10 gap-y-7 py-14 md:py-16 list-none"
                        data-testid="marquee-clients-grid"
                    >
                        {[
                            "Accenture",
                            "Adobe",
                            "Bosch",
                            "Capgemini",
                            "CGI",
                            "Deloitte",
                            "Dell",
                            "Deutsche Bank",
                            "Ernst & Young",
                            "Ford",
                            "Freescale Semiconductor",
                            "GE",
                            "Hexaware Technologies",
                            "Hiranandani Palace Gardens",
                            "HSBC",
                            "Hyundai",
                            "IBM",
                            "iGATE Patni",
                            "Intel",
                            "ITC Limited",
                            "JPMorgan",
                            "KPMG",
                            "Mahindra",
                            "Microsoft",
                            "Nokia",
                            "PricewaterhouseCoopers",
                            "RBS",
                            "Saint-Gobain",
                            "Shell",
                            "Siemens",
                            "Thomson Reuters",
                            "Unilever",
                        ].map((name) => (
                            <li
                                key={name}
                                className="font-serif italic text-[18px] md:text-[20px] leading-snug text-[hsl(var(--ink))] tracking-tight"
                                data-testid={`client-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            >
                                {name}
                            </li>
                        ))}
                    </ul>

                    <div className="gold-rule" style={{ background: "hsl(var(--brass) / 0.35)" }} />

                    <p className="mt-8 text-[13px] text-[hsl(var(--muted))] text-center tracking-[0.05em]">
                        And many more we are not at liberty to name. Discretion is part of the service.
                    </p>
                </div>
            </section>

            {/* 04.5 — THE INVISIBLE WORKLOAD */}
            <section
                className="section"
                style={{ background: "hsl(var(--paper))" }}
                data-testid="section-invisible-workload"
            >
                <div className="container-prose max-w-4xl">
                    <p className="eyebrow">Why this exists</p>
                    <h2
                        className="font-serif text-4xl md:text-[56px] leading-[1.08] mt-6 max-w-3xl"
                        data-testid="invisible-workload-headline"
                    >
                        {v("invisible_workload_headline")}
                    </h2>
                    <div
                        className="mt-12 space-y-8 font-serif text-[20px] md:text-[24px] leading-[1.55] text-[hsl(var(--navy))]"
                        data-testid="invisible-workload-body"
                    >
                        {v("invisible_workload_body")
                            .split(/\n\n+/)
                            .map((para, i) => (
                                <p key={i}>{para}</p>
                            ))}
                    </div>
                </div>
            </section>

            {/* 05 — WHAT WE DELIVER */}
            <section
                className="section"
                style={{ background: "hsl(var(--cream))" }}
                data-testid="section-services"
            >
                <div className="container-prose">
                    <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
                        <div className="md:col-span-5">
                            <p className="eyebrow">Section 05 · What we deliver</p>
                            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
                                Six Pillars. <br />
                                <span className="italic text-[hsl(var(--brass))]">One Promise.</span>
                            </h2>
                            <p className="mt-6 font-serif italic text-[18px] md:text-[20px] leading-snug text-[hsl(var(--brass))]">
                                To help employees manage life better, so they can bring their best selves to work.
                            </p>
                        </div>
                        <p className="md:col-span-6 md:col-start-7 text-[16px] md:text-[17px] leading-relaxed text-[hsl(var(--ink))]">
                            Every request begins with Mira, who gathers and structures the need. But every outcome is handled by a trained Les Concierges® Concierge® — who confirms, sources, coordinates and closes the loop through real human follow-through.
                            <br /><br />
                            Nothing important is left to a chatbot. Nothing is treated as too small. Nothing is passed back to the employee to figure out alone.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-x-12 gap-y-12">
                        {services.map((s, i) => (
                            <Link
                                key={s.slug}
                                to={`/solutions/${s.slug}`}
                                className="tile group"
                                data-testid={`service-tile-${s.slug}`}
                            >
                                <div className="flex items-baseline gap-3">
                                    <span
                                        className="font-serif italic text-[hsl(var(--brass))] text-xl"
                                    >
                                        0{i + 1}
                                    </span>
                                    <h3 className="font-serif text-2xl md:text-[26px] leading-snug">
                                        {s.title}
                                    </h3>
                                </div>
                                <p className="mt-4 text-[15px] leading-relaxed text-[hsl(var(--ink))] max-w-md">
                                    {s.body}
                                </p>
                                <p className="mt-5 eyebrow group-hover:text-[hsl(var(--brass))] transition-colors">
                                    Read more →
                                </p>
                            </Link>
                        ))}
                    </div>

                    {/* Concierge difference closer */}
                    <div className="mt-24 md:mt-28 max-w-4xl mx-auto text-center" data-testid="concierge-difference">
                        <div className="gold-rule mx-auto" style={{ width: "60px" }} />
                        <p className="font-serif italic text-[22px] md:text-[28px] leading-relaxed mt-10 text-[hsl(var(--navy))]">
                            Mira brings intelligence. <br />
                            The Concierge® brings judgement. <br />
                            The human team brings trust, empathy and execution.
                        </p>
                        <p className="mt-10 text-[15px] md:text-[16px] leading-relaxed text-[hsl(var(--ink))] max-w-2xl mx-auto">
                            For HR, this means a benefit employees actually use. For employees, it means life becomes easier to manage.
                        </p>
                    </div>
                </div>
            </section>

            {/* 06 — FROM THE OPERATIONS FLOOR */}
            <section className="section" data-testid="section-operations-floor">
                <div className="container-prose">
                    <p className="eyebrow">Section 06 · From the operations floor</p>
                    <h2 className="font-serif text-4xl md:text-5xl mt-4 max-w-4xl leading-tight" data-testid="operations-floor-heading">
                        Mira listens when life <span className="italic text-[hsl(var(--brass))]">interrupts work.</span>
                    </h2>

                    <p className="mt-10 max-w-3xl font-serif italic text-[20px] md:text-[24px] leading-[1.55] text-[hsl(var(--navy))]" data-testid="operations-floor-lede">
                        A parent's school deadline. A doctor's appointment. A passport renewal. A home repair. A travel change. Each request is gathered with care and handed to our human concierge® team, who coordinates, follows up, and closes the loop. Employees stay focused. Life still gets handled.
                    </p>

                    <div className="mt-20 grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-14" data-testid="operations-floor-grid">
                        {[
                            {
                                tag: "Elder care",
                                quote: "Mira listened when an employee said her elderly father needed help with a diagnostic test, but she could not leave the office immediately. Our concierge® team understood the urgency, coordinated the appointment, confirmed the details, and stayed with the request until the family had what they needed.",
                                close: "That is the quiet value of employee concierge.",
                            },
                            {
                                tag: "The invisible load",
                                quote: "An employee came in with three small requests: a bill payment issue, a courier follow-up, and an appointment to reschedule. None of them looked dramatic. Together, they would have taken half a day. Mira gathered them clearly, and our concierge® team closed each loop quietly.",
                                close: "That is the invisible load we help remove.",
                            },
                            {
                                tag: "Doctor appointment",
                                quote: "Mira listened when an employee said, ‘I can't keep stepping out of meetings to make calls.' Her mother needed a doctor's appointment urgently. Mira gathered the details, our concierge® team coordinated the appointment, confirmed the timing, shared the address, and followed up after the visit.",
                                close: "Work continued. The worry became lighter.",
                            },
                            {
                                tag: "Passport renewal",
                                quote: "An employee had a passport renewal pending, a document checklist she did not understand, and no time to stand in queues. Mira gathered the requirement, our concierge® team explained what was needed, helped organise the process, and kept her updated until the appointment was completed.",
                                close: "What felt like a full-day problem became a managed task.",
                            },
                            {
                                tag: "Home repair",
                                quote: "An employee had a leaking pipe at home and no one available to coordinate the repair. Mira gathered the issue, timing, location, and urgency. Our concierge® team stepped in, coordinated the right support, followed through, and kept the employee informed without repeated calls.",
                                close: "The problem was handled while the workday stayed intact.",
                            },
                            {
                                tag: "Government documentation",
                                quote: "An employee needed help understanding a government documentation process and had already lost hours trying to figure it out. Mira gathered the request. Our concierge® team broke down the steps, clarified the documents required, and helped him move forward without confusion.",
                                close: "Sometimes relief is simply knowing what to do next.",
                            },
                        ].map((v) => (
                            <figure
                                key={v.tag}
                                className="flex flex-col"
                                data-testid={`vignette-${v.tag.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                <p className="eyebrow">{v.tag}</p>
                                <blockquote
                                    className="mt-5 font-serif text-[16px] md:text-[17px] leading-[1.7] text-[hsl(var(--ink))]"
                                    style={{ borderLeft: "1px solid hsl(var(--brass) / 0.5)", paddingLeft: "1.25rem" }}
                                >
                                    "{v.quote}"
                                </blockquote>
                                <p className="mt-5 pl-5 font-serif italic text-[15px] md:text-[16px] text-[hsl(var(--brass))]">
                                    {v.close}
                                </p>
                                <figcaption className="mt-6 pl-5 eyebrow" style={{ color: "hsl(var(--muted))" }}>
                                    Operations Floor · Les Concierges®
                                </figcaption>
                            </figure>
                        ))}
                    </div>

                    <p className="mt-20 max-w-2xl font-serif italic text-[20px] md:text-[22px] leading-snug text-[hsl(var(--navy))]" data-testid="operations-floor-coda">
                        Not luxury. Relief.
                    </p>
                </div>
            </section>

            {/* 07 — BUILT FOR INDIA */}
            <section
                className="section-tight"
                style={{ background: "hsl(var(--cream))" }}
                data-testid="section-india"
            >
                <div className="container-prose">
                    <p className="eyebrow">Section 07 · Built for India · And 150+ countries beyond</p>
                    <div className="mt-10 divide-y divide-[hsl(var(--brass)/0.3)]">
                        {[
                            {
                                k: "Languages your employees speak.",
                                v: "Mira responds in English, Hindi, and major Indian languages without forcing anyone to translate themselves into corporate speak.",
                            },
                            {
                                k: "Across India. Across the world.",
                                v: "We operate across India — north to south, metros to towns to tier-2 cities — and complete requests in over 150 countries through our partner network. The same standard of care, anywhere your people travel. No matter what. No matter when.",
                            },
                            {
                                k: "WhatsApp-first.",
                                v: "Most employees never visit a portal. Mira lives where they already are — on WhatsApp, with email and call-back as backup.",
                            },
                            {
                                k: "Built for stressed-out employees.",
                                v: "We exist for the bills, the visa runs, the school admissions, the doctor home-visits, the last-minute anniversary saves. Twenty-eight years of giving employees back the lunch hours, evenings and weekends they would otherwise lose to errands.",
                            },
                        ].map((row, idx) => (
                            <div
                                key={idx}
                                className="grid md:grid-cols-12 gap-6 md:gap-12 py-8"
                                data-testid={`india-row-${idx}`}
                            >
                                <p className="md:col-span-4 font-serif text-[22px] md:text-[26px] leading-snug">
                                    {row.k}
                                </p>
                                <p className="md:col-span-7 md:col-start-6 text-[15px] md:text-[16px] leading-relaxed text-[hsl(var(--ink))]">
                                    {row.v}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 08 — THREE WAYS WE HELP EMPLOYEES */}
            <section className="section" data-testid="section-personas">
                <div className="container-prose">
                    <p className="eyebrow">Section 08 · Who it is for</p>
                    <h2 className="font-serif text-4xl md:text-5xl mt-4 max-w-4xl leading-tight" data-testid="personas-heading">
                        Three ways we help employees. <span className="italic text-[hsl(var(--brass))]">One standard of care.</span>
                    </h2>

                    {/* Intro — the lyrical opening */}
                    <div className="mt-14 max-w-3xl">
                        <p className="font-serif text-[20px] md:text-[24px] leading-[1.6] text-[hsl(var(--navy))]" data-testid="personas-intro-lede">
                            Les Concierges® exists for the real life employees carry alongside work.
                        </p>

                        <ul className="mt-10 space-y-3 font-serif text-[18px] md:text-[20px] leading-[1.5] text-[hsl(var(--ink))]" data-testid="personas-life-moments">
                            {lifeMoments.map((line) => (
                                <li key={line} className="italic" style={{ color: "hsl(var(--ink) / 0.85)" }}>
                                    {line}
                                </li>
                            ))}
                        </ul>

                        <p className="mt-12 text-[15px] md:text-[16px] leading-relaxed text-[hsl(var(--ink))]" data-testid="personas-intro-mira">
                            Across every need, the promise stays the same: Mira understands and structures the request. A trained Les Concierges® Concierge® confirms, sources, coordinates and closes the loop.
                        </p>

                        <div className="mt-10 space-y-2 font-serif italic text-[18px] md:text-[20px] text-[hsl(var(--brass))]" data-testid="personas-triplet">
                            <p>Human where it matters.</p>
                            <p>Digital where it helps.</p>
                            <p>Available wherever life happens.</p>
                        </div>
                    </div>

                    {/* The three persona cards */}
                    <div className="mt-20 grid md:grid-cols-3 gap-10 md:gap-8">
                        {personas.map((p) => (
                            <div
                                key={p.eyebrow}
                                className="persona-card flex flex-col"
                                data-testid={`persona-${p.eyebrow.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                <p className="eyebrow">{p.eyebrow}</p>
                                <p className="mt-6 font-serif text-[18px] md:text-[20px] leading-[1.45] text-[hsl(var(--navy))]">
                                    {p.lede}
                                </p>
                                <p className="mt-5 text-[15px] leading-relaxed text-[hsl(var(--ink))]">
                                    {p.body}
                                </p>
                                <p className="mt-6 font-serif italic text-[16px] leading-snug text-[hsl(var(--brass))]">
                                    {p.close}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 08b — THE LES CONCIERGES® PROMISE */}
            <section
                className="section"
                style={{ background: "hsl(var(--navy))", color: "hsl(var(--ivory))" }}
                data-testid="section-promise"
            >
                <div className="container-prose max-w-3xl">
                    <p className="eyebrow" style={{ color: "hsl(var(--brass))" }}>
                        The Les Concierges® Promise
                    </p>

                    <ul
                        className="mt-12 space-y-4 font-serif text-[22px] md:text-[28px] leading-[1.4]"
                        style={{ color: "hsl(var(--ivory))" }}
                        data-testid="promise-denials"
                    >
                        {promiseDenials.map((line) => (
                            <li key={line} className="italic" style={{ color: "hsl(var(--ivory) / 0.78)" }}>
                                {line}
                            </li>
                        ))}
                    </ul>

                    <p
                        className="mt-12 font-serif text-[24px] md:text-[32px] leading-[1.35]"
                        style={{ color: "hsl(var(--brass))" }}
                        data-testid="promise-headline"
                    >
                        We are the human layer behind the everyday life of employees.
                    </p>

                    <div
                        className="mt-12 space-y-6 text-[16px] md:text-[17px] leading-[1.7]"
                        style={{ color: "hsl(var(--ivory) / 0.85)" }}
                        data-testid="promise-body"
                    >
                        <p>
                            For twenty-eight years, Les Concierges® has helped people get things done, quietly and reliably. Mira now makes that support sharper by remembering, structuring and anticipating needs.
                        </p>
                        <p>
                            But every meaningful request still ends where it should: with a trained human Concierge® who understands urgency, judgement, discretion and care.
                        </p>
                    </div>

                    <div
                        className="mt-14 pt-10"
                        style={{ borderTop: "1px solid hsl(var(--brass) / 0.45)" }}
                        data-testid="promise-closing"
                    >
                        <p className="font-serif text-[20px] md:text-[24px] italic leading-[1.5]" style={{ color: "hsl(var(--ivory))" }}>
                            Employees feel supported. <span style={{ color: "hsl(var(--brass))" }}>HR sees engagement.</span> Companies earn loyalty.
                        </p>
                    </div>
                </div>
            </section>


            {/* 09 — MANIFESTO */}
            <section
                className="section"
                style={{ background: "hsl(var(--paper))" }}
                data-testid="section-manifesto"
            >
                <div className="container-prose max-w-3xl">
                    <p className="eyebrow text-center">The Manifesto</p>
                    <div
                        className="font-serif text-[19px] md:text-[24px] leading-[1.6] mt-12 text-[hsl(var(--navy))] space-y-8"
                        data-testid="manifesto-text"
                    >
                        {v("manifesto")
                            .split(/\n\n+/)
                            .map((para, i) => (
                                <p key={i}>{para}</p>
                            ))}
                    </div>
                </div>
            </section>

            {/* 10 — RECOGNITION */}
            <section
                className="section-tight"
                data-testid="section-recognition"
                style={{ paddingTop: "4rem", paddingBottom: "4rem" }}
            >
                <div className="container-prose">
                    <div className="gold-rule" />
                    <div className="py-14 md:py-16 text-center">
                        <div className="flex items-center justify-center gap-12 md:gap-20 flex-wrap">
                            {["APEA", "Asia's Greatest Brands", "ET Pioneers", "BW BusinessWorld"].map((b) => (
                                <span
                                    key={b}
                                    className="font-serif text-[18px] md:text-[20px] tracking-tight"
                                    style={{
                                        color: "hsl(var(--muted))",
                                        borderTop: "1px solid hsl(var(--brass) / 0.5)",
                                        borderBottom: "1px solid hsl(var(--brass) / 0.5)",
                                        padding: "10px 18px",
                                        fontStyle: "italic",
                                    }}
                                    data-testid={`award-${b.replace(/[^a-z]/gi, "")}`}
                                >
                                    {b}
                                </span>
                            ))}
                        </div>
                        <p
                            className="mt-10 max-w-3xl mx-auto text-[14px] leading-relaxed"
                            style={{ color: "hsl(var(--muted))" }}
                            data-testid="recognition-line"
                        >
                            {v("recognition_line")}
                        </p>
                    </div>
                    <div className="gold-rule" />
                </div>
            </section>

            {/* 11 — ONE CLEAR NEXT STEP */}
            <section
                id="pilot"
                className="section"
                style={{ background: "hsl(var(--cream))" }}
                data-testid="section-pilot"
            >
                <div className="container-prose max-w-4xl">
                    <p className="eyebrow text-center">Section 11 · A private pilot</p>
                    <h2 className="font-serif text-4xl md:text-5xl mt-6 leading-tight text-center">
                        One clear next step.
                    </h2>
                    <div className="mt-12">
                        <PilotForm
                            invitation={v("form_invitation")}
                            whatsappAlt={v("whatsapp_alt")}
                            whatsappNumber={v("whatsapp_number", "919739908844")}
                        />
                    </div>
                </div>
            </section>
        </>
    );
}
